import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import './About.scss'

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import renderOptions from '../utils/textRenderOption';
import { motion, AnimatePresence } from 'framer-motion'
import { getSmallImageUrl } from '../utils/imageLoader';
import { useLenisScroll } from '../utils/lenisConfig';

const About = ({ items = [] }) => {
    const [expandedId, setExpandedId] = useState(null);
    const [hideNonProposTitles, setHideNonProposTitles] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({});

    // Lenis smooth scrolling (match other pages)
    useLenisScroll();

    // Sort items to put 'à propos' first
    const sortedItems = [...items].sort((a, b) => {
        if (a.titre.toLowerCase().includes('propos')) return -1;
        if (b.titre.toLowerCase().includes('propos')) return 1;
        return 0;
    });

    // Auto-expand first item when page loads
    useEffect(() => {
        if (items.length > 0) {
            const firstSorted = [...items].sort((a, b) => {
                if (a.titre.toLowerCase().includes('propos')) return -1;
                if (b.titre.toLowerCase().includes('propos')) return 1;
                return 0;
            })
            setExpandedId(firstSorted[0].id)
        }
    }, [items])

    // Listen for external requests to close about texts (e.g. when shop menu opens)
    useEffect(() => {
        const handleClose = () => {
            setExpandedId(null)
            setHideNonProposTitles(true)
        }
        window.addEventListener('closeAboutTexts', handleClose)
        return () => window.removeEventListener('closeAboutTexts', handleClose)
    }, [])

    const toggleItem = (id) => {
        setExpandedId(prev => (prev === id ? null : id));
        setHideNonProposTitles(false);
        // Close shop menu when opening about items
        window.dispatchEvent(new Event('closeShopMenu'));
    };

    // Load image dimensions for all about images
    useEffect(() => {
        items.forEach((item) => {
            if (item.images && item.images.length > 0) {
                item.images.forEach((img, idx) => {
                    const imageKey = `${item.id}-${idx}`;
                    const imageUrl = getSmallImageUrl(img);
                    if (!imageUrl || imageDimensions[imageKey]) return;

                    const imgElement = new Image();
                    imgElement.onload = () => {
                        setImageDimensions(prev => ({
                            ...prev,
                            [imageKey]: {
                                width: imgElement.naturalWidth,
                                height: imgElement.naturalHeight
                            }
                        }));
                    };
                    imgElement.src = imageUrl;
                });
            }
        });
    }, [items, imageDimensions]);



    return (
        <div className='aboutPage'>
            <Helmet>
                <title>A propos | Marguerite Soulier</title>
                <link rel="canonical" href="https://margueritesoulier.fr/a-propos" />
                <meta name="description" content="Découvrez Marguerite Soulier : artiste, designer textile et bergère saisonnière." />
                <meta property="og:url" content="https://margueritesoulier.fr/a-propos" />
            </Helmet>
            <div className='aboutContent'>
                <div className='aboutMenu'>
                    {sortedItems.map((item) => {
                        const isPropos = item.titre.toLowerCase().includes('propos');
                        const shouldShow = !hideNonProposTitles || isPropos;

                        if (!shouldShow) return null;

                        return (
                            <div key={item.id} className='aboutMenuItem'>
                                <div
                                    className={`aboutMenuTitle ${expandedId === item.id ? 'expanded' : ''}`}
                                    onClick={() => toggleItem(item.id)}
                                >
                                    {item.titre}
                                </div>
                                <AnimatePresence initial={false}>
                                    {expandedId === item.id && (
                                        <motion.div
                                            key={item.id}
                                            className='aboutMenuContent'
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            {item.texte && (
                                                <div className='aboutText'>
                                                    {documentToReactComponents(item.texte, renderOptions)}
                                                </div>
                                            )}
                                            {item.images && item.images.length > 0 && (
                                                <div className='aboutImages'>
                                                    {item.images.map((img, idx) => {
                                                        const imageKey = `${item.id}-${idx}`;
                                                        return (
                                                            <img
                                                                key={idx}
                                                                src={getSmallImageUrl(img)}
                                                                alt={item.titre}
                                                                {...(imageDimensions[imageKey]?.width && { width: imageDimensions[imageKey].width })}
                                                                {...(imageDimensions[imageKey]?.height && { height: imageDimensions[imageKey].height })}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    )
}

export default About